'use client';

/**
 * CreateItemSlideOver — quick-create a catalog item from a document line.
 *
 * Much simpler sibling of CreateInventoryItemModal: just enough fields to
 * register a sellable/costable item (name, code, type, prices, unit) so the
 * document line can link to a real catalog item instead of a free-form code.
 *
 * Posts to POST /api/v1/work-items (item_type='inventory'), which also fires
 * the external-sync hook for inventory items. `onCreated` receives the raw
 * created work_items row so the caller can map + select it.
 */

import React, { useEffect, useState } from 'react';
import { Package, AlertCircle } from 'lucide-react';
import { ConfigSlideOver, SlideOverFooter, SlideOverButton, useSlideOverChrome } from '@/components/SlideOver';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';
import type { WorkItemInventory } from './ItemSearchModal';

export interface CreateItemSlideOverProps {
  onClose: () => void;
  /** Called with the created work_items row — caller maps to FinanceItem. */
  onCreated: (workItem: WorkItemInventory) => void;
  /** Prefill for the item name (usually the term that found no match). */
  prefillName?: string;
  /** Prefill for the item code (usually the same term if it looks code-like). */
  prefillCode?: string;
  currency?: string;
  /** Backdrop z-index; panel renders at zIndex + 1. DocumentModal's root is
      z-[130] so the default stacks this slide-over above it. */
  zIndex?: number;
}

interface FormState {
  item_subtype: 'service' | 'product';
  title: string;
  item_number: string;
  description: string;
  unit_of_measure: string;
  selling_price: string;
  cost_price: string;
  currency: string;
  track_inventory: boolean;
}

const UNIT_OPTIONS = ['each', 'unit', 'hour', 'meter', 'kg', 'litre', 'box', 'pack', 'set'];
const CURRENCY_OPTIONS = ['ZAR', 'USD', 'EUR', 'GBP'];

export default function CreateItemSlideOver({
  onClose,
  onCreated,
  prefillName,
  prefillCode,
  currency = 'ZAR',
  zIndex = 140,
}: CreateItemSlideOverProps) {
  const { isDarkMode } = useSlideOverChrome();
  const [form, setForm] = useState<FormState>({
    item_subtype: 'service',
    title: prefillName || '',
    item_number: prefillCode || '',
    description: '',
    unit_of_measure: 'each',
    selling_price: '',
    cost_price: '',
    currency: currency || 'ZAR',
    track_inventory: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      title: prev.title || prefillName || '',
      item_number: prev.item_number || prefillCode || '',
    }));
  }, [prefillName, prefillCode]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    setError(null);
    if (!form.title.trim()) {
      setError('Item name is required');
      return;
    }

    setSaving(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        setError('Authentication required');
        return;
      }

      const isProduct = form.item_subtype === 'product';
      const res = await fetch('/api/v1/work-items', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'create',
          item_type: 'inventory',
          item_subtype: form.item_subtype,
          title: form.title.trim(),
          description: form.description.trim() || undefined,
          item_number: form.item_number.trim() || undefined,
          unit_of_measure: form.unit_of_measure || 'each',
          selling_price: form.selling_price ? Number(form.selling_price) : null,
          cost_price: form.cost_price ? Number(form.cost_price) : null,
          currency: form.currency || 'ZAR',
          track_inventory: isProduct ? form.track_inventory : false,
          status: 'active',
          custom_fields: {
            source: 'document_line_item_create',
          },
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to create item');
      }

      notify.success(`Item "${form.title.trim()}" created`);
      onCreated(data.data as WorkItemInventory);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create item');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = `w-full px-3 py-2 rounded-lg border text-sm transition-colors ${
    isDarkMode
      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  } focus:outline-none focus:ring-2 focus:ring-blue-500`;
  const labelClass = `block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;

  return (
    <ConfigSlideOver
      onClose={onClose}
      icon={Package}
      title="Create Item"
      subtitle="Register a catalog item to use on this line"
      zIndex={zIndex}
      footer={
        <SlideOverFooter summary={error ? undefined : 'Codes must reference a catalog item'}>
          <SlideOverButton variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </SlideOverButton>
          <SlideOverButton
            variant="primary"
            icon={Package}
            spinning={saving}
            disabled={saving || !form.title.trim()}
            onClick={handleSubmit}
          >
            {saving ? 'Creating…' : 'Create & Select'}
          </SlideOverButton>
        </SlideOverFooter>
      }
    >
      <div className="space-y-4">
        {/* Type toggle */}
        <div>
          <label className={labelClass}>Item Type</label>
          <div className={`inline-flex rounded-lg border overflow-hidden ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}>
            {(['service', 'product'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => update('item_subtype', t)}
                className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${
                  form.item_subtype === t
                    ? 'bg-blue-600 text-white'
                    : isDarkMode
                      ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {t === 'service' ? 'Service' : 'Product'}
              </button>
            ))}
          </div>
        </div>

        {/* Name */}
        <div>
          <label className={labelClass}>Item Name *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => update('title', e.target.value)}
            placeholder="e.g. Standard Labour"
            className={inputClass}
            autoFocus
          />
        </div>

        {/* Code + Unit */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Item Code</label>
            <input
              type="text"
              value={form.item_number}
              onChange={(e) => update('item_number', e.target.value)}
              placeholder="Auto-assigned if blank"
              className={`${inputClass} font-mono`}
            />
          </div>
          <div>
            <label className={labelClass}>Unit</label>
            <select
              value={form.unit_of_measure}
              onChange={(e) => update('unit_of_measure', e.target.value)}
              className={inputClass}
            >
              {UNIT_OPTIONS.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className={labelClass}>Description</label>
          <textarea
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
            placeholder="Optional description…"
            rows={2}
            className={inputClass}
          />
        </div>

        {/* Prices */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={labelClass}>Selling Price</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.selling_price}
              onChange={(e) => update('selling_price', e.target.value)}
              placeholder="0.00"
              className={`${inputClass} font-mono`}
            />
          </div>
          <div>
            <label className={labelClass}>Cost Price</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.cost_price}
              onChange={(e) => update('cost_price', e.target.value)}
              placeholder="0.00"
              className={`${inputClass} font-mono`}
            />
          </div>
          <div>
            <label className={labelClass}>Currency</label>
            <select
              value={form.currency}
              onChange={(e) => update('currency', e.target.value)}
              className={inputClass}
            >
              {CURRENCY_OPTIONS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Stock tracking — products only */}
        {form.item_subtype === 'product' && (
          <label className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              checked={form.track_inventory}
              onChange={(e) => update('track_inventory', e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Track stock on hand
            </span>
          </label>
        )}

        {error && (
          <p className="text-sm text-red-500 flex items-center gap-1.5">
            <AlertCircle size={16} /> {error}
          </p>
        )}
      </div>
    </ConfigSlideOver>
  );
}
